<template>
    <div ref="baseScrollContainer"
         :class="{ 'base-scroll-container--in-modal' : isInModal }"
         class="base-scroll-container"
    >
        <div ref="baseScrollBody"
             class="base-scroll-container__body"
             :class="{
                 'base-scroll-container__body--fixed-header' : isHeaderFixed,
                 'base-scroll-container__body--fixed-footer' : isFooterFixed,
                 'base-scroll-container__body--in-modal' : isInModal,
             }"
             :style="{
                 maxHeight: scrollContainerBodyHeight + 'px'
             }"
             @scroll="amountScrolled"
        >
            <slot />
        </div>
    </div>
</template>

<script>
export default {
    props: {
        bodyHeight: {
            type: Number,
            default: undefined,
        },
        isInModal: {
            type: Boolean,
            default: false,
        },
        hasBoxShadow: {
            type: Boolean,
            default: true,
        },
        hasFooter: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            trackLength: 0,
            percentageScrolled: 0,
            throttleScroll: undefined,
            scrollContainerBodyHeight: this.bodyHeight || undefined,
        };
    },
    computed: {
        isFooterFixed() {
            return this.percentageScrolled < 100
            && this.trackLength > 0
            && this.hasBoxShadow
            && this.hasFooter;
        },
        isHeaderFixed() {
            return this.percentageScrolled > 0 && this.hasBoxShadow;
        },
    },
    created() {
        window.addEventListener('resize', () => {
            this.amountScrolled();
        }, false);
    },
    mounted() {
        // Next tick is needed for modals as they need to be registered first
        this.$nextTick(() => {
            this.amountScrolled();
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', () => {
            this.amountScrolled();
        }, false);
    },
    methods: {
        getMeasurements() {
            if (!this.scrollContainerBodyHeight) {
                if (this.isInModal) {
                    this.calculateMaxHeightForModal();
                } else {
                    this.scrollContainerBodyHeight = 500;
                }
            }

            // Shadow will be removed 10px before the end. Fixes edge cases with percentages.
            const containerBodyHeight = this.getContainerBodyHeight() - 10;
            const scrollContainerBodyHeight = this.scrollContainerBodyHeight
                ? this.scrollContainerBodyHeight : containerBodyHeight;

            this.trackLength = containerBodyHeight - scrollContainerBodyHeight;
        },
        amountScrolled() {
            this.getMeasurements();

            const baseScrollBody = this.$refs.baseScrollBody;
            const scrollTop = (this.$refs.baseScrollBody && this.$refs.baseScrollBody.scrollTop) || 0;

            const percentageScrolled = scrollTop / this.trackLength * 100;
            const percentageScrolledWithBottomScrollbar = scrollTop
                / (this.trackLength + this.getScrollbarHeight()) * 100;

            if (!this.hasHorizontalScrollbar(baseScrollBody)) {
                this.percentageScrolled = percentageScrolled;
            } else {
                this.percentageScrolled = percentageScrolledWithBottomScrollbar;
            }
        },
        getContainerBodyHeight() {
            const baseScrollBody = this.$refs.baseScrollBody;

            return baseScrollBody ? baseScrollBody.scrollHeight : 0;
        },
        getScrollbarHeight() {
            const baseScrollBody = this.$refs.baseScrollBody;

            return baseScrollBody ? baseScrollBody.offsetHeight - baseScrollBody.clientHeight : 0;
        },
        hasHorizontalScrollbar(object) {
            return object && (object.scrollWidth > object.clientWidth);
        },
        // Getting base vue modal from the bottom to top in case there are multiple (not yet closed) modals.
        getBaseModal() {
            return this.$modalManager
                && this.$modalManager.instances[0]
                && this.$modalManager.instances[0].modal.$parent
                && this.$modalManager.instances[0].modal.$parent.$parent.$el;
        },
        calculateMaxHeightForModal() {
            const manager = this.$modalManager;
            const modal = this.getBaseModal();

            if (!modal || !manager) {
                return;
            }

            const modalOverlayHeight = window.innerHeight || undefined;
            const modalHeight = modal.querySelector('.v--modal-box.v--modal') ? modal.querySelector('.v--modal-box.v--modal').scrollHeight : undefined;
            const modalScrollBodySize = this.getContainerBodyHeight();

            if (modalOverlayHeight && modalScrollBodySize && modalHeight) {
                const calculatedHeight = modalOverlayHeight
                    - (modalHeight - modalScrollBodySize) - 3; // -3 is to counteract browsers' offset
                this.scrollContainerBodyHeight = calculatedHeight > 200 ? calculatedHeight : 200;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.base-scroll-container {
    position: relative;
    $_this: &;

    &--in-modal {
        margin: 0 (-$modal-padding-sides);
    }

    &__body {
        overflow-y: auto;

        /deep/ {
            .table {
                margin: 0;
            }
        }

        &--in-modal {
            padding: 0 $modal-padding-sides 40px;
        }

        &--fixed-header,
        &--fixed-footer {
            &::before {
                content: '';
                display: block;
                position: absolute;
                z-index: 2;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
            }
        }

        &--fixed-header {
            &::before {
                box-shadow: $box-shadow-inner-modal-top;
            }

            &#{$_this}__body--in-modal {
                &::before {
                    box-shadow: $box-shadow-inner-modal-top;
                }
            }
        }

        &--fixed-footer {
            &::before {
                box-shadow: $box-shadow-inner-modal-bottom;
            }

            &#{$_this}__body--in-modal {
                &::before {
                    box-shadow: $box-shadow-inner-modal-bottom;
                }
            }

            &#{$_this}__body--fixed-header {
                &::before {
                    box-shadow:
                        $box-shadow-inner-modal-top,
                        $box-shadow-inner-modal-bottom;
                }

                &#{$_this}__body--in-modal {
                    &::before {
                        box-shadow:
                            $box-shadow-inner-modal-top,
                            $box-shadow-inner-modal-bottom;
                    }
                }
            }
        }
    }
}
</style>
